import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './sidebar';

const WheelDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [wheelData, setWheelData] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const wheelId = new URLSearchParams(location.search).get('key');

    useEffect(() => {
        if (wheelId) {
            fetchWheelDetails();
        }
    }, [wheelId]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const formatPrice = (price) => {
        if (!price) return '-';
        return price.includes('$') ? price : `$${price}`;
    };

    const fetchWheelDetails = async () => {
        try {
            console.log("Fetching details for wheel:", wheelId);
            
            const response = await axios.post(process.env.REACT_APP_API_URL, {
                apikey: process.env.REACT_APP_API_KEY,
                module: "twheel",
                method: "findwheel",
                wheel_id: wheelId
            });

            const jsonData = JSON.parse(response.data.body);
            if (jsonData.status === "success") {
                setWheelData(jsonData.data[0]);
                console.log("Wheel data:", jsonData.data[0]);
            } else {
                setError("Failed to fetch wheel details");
            }
        } catch (err) {
            console.error("Error fetching wheel details:", err);
            setError("Error loading wheel details");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="wheel-details-page">
            <style>
                {`
                    /* Base layout */
                    .wheel-details-page {
                        display: flex;
                        min-height: 100vh;
                        width: 100%;
                        overflow-x: hidden;
                    }

                    .main-content {
                        flex: 1;
                        padding: 20px;
                        background: #f5f5f5;
                        overflow-y: auto;
                        height: 100vh;
                    }

                    /* Container styles */
                    .details-container {
                        max-width: 770px;
                        margin: 0 auto;
                        background: white;
                        padding: 20px;
                        border-radius: 8px;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        padding-left: 8%;
                    }

                    /* Grid layout */
                    .detail-section {
                        margin-bottom: 25px;
                    }

                    .section-title {
                        color: #1a237e;
                        font-size: 1.2rem;
                        margin-bottom: 15px;
                        padding-bottom: 8px;
                        border-bottom: 1px solid #e0e0e0;
                    }

                    .detail-grid {
                        display: grid;
                        gap: 15px;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    }

                    /* Item styles */
                    .detail-item {
                        background: #f8f9fa;
                        padding: 15px;
                        border-radius: 6px;
                        display: flex;
                        flex-direction: column;
                    }

                    .detail-label {
                        font-size: 0.85rem;
                        color: #666;
                        margin-bottom: 5px;
                    }

                    .detail-value {
                        font-size: 1rem;
                        color: #333;
                        word-break: break-word;
                    }

                    /* Status styles */
                    .status-badge {
                        display: inline-block;
                        padding: 6px 12px;
                        border-radius: 15px;
                        font-size: 0.75rem;
                        font-weight: 500;
                        text-transform: uppercase;
                        align-self: flex-start;
                    }

                    .status-published {
                        background: #e8f5e9;
                        color: #2e7d32;
                    }

                    .status-wfa {
                        background: #fff3e0;
                        color: #e65100;
                    }

                    /* Pie options */
                    .pies-container {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                    }

                    .pie-item {
                        background: #e3f2fd;
                        padding: 6px 12px;
                        border-radius: 15px;
                        font-size: 0.85rem;
                        color: #1565c0;
                    }

                    /* Back button */
                    .back-button {
                        margin-bottom: 20px;
                        padding: 8px 16px;
                        background: #1a237e;
                        color: white;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 0.9rem;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        transition: background 0.3s;
                    }

                    .back-button:hover {
                        background: #303f9f;
                    }

                    /* Responsive adjustments */
                    @media screen and (max-width: 768px) {
                        .wheel-details-page {
                            flex-direction: column;
                        }

                        .main-content {
                            padding: 15px;
                            height: auto;
                        }

                        .details-container {
                            padding: 15px;
                        }

                        .detail-grid {
                            grid-template-columns: 1fr;
                        }

                        .section-title {
                            font-size: 1.1rem;
                        }

                        .detail-item {
                            padding: 12px;
                        }
                    }

                    /* Loading state */
                    .initial-loading {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        min-height: 50vh;
                    }

                    .loading-spinner {
                        width: 40px;
                        height: 40px;
                        border: 3px solid rgba(26, 35, 126, 0.1);
                        border-radius: 50%;
                        border-top-color: #1a237e;
                        animation: spin 1s ease-in-out infinite;
                    }

                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>

            <Sidebar />
            <div className="main-content">
                {isLoading ? (
                    <div className="initial-loading">
                        <div className="loading-logo">SpinWin</div>
                        <div className="loading-spinner"></div>
                        <div className="loading-text">Loading spin Details</div>
                    </div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : wheelData && (
                    <>
                        <div className="details-container">
                            <div className="detail-section">
                                <h2 className="section-title">Wheel Information</h2>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">Wheel Name</div>
                                        <div className="detail-value">{wheelData.wheel_name}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Status</div>
                                        <div className="detail-value">
                                            <span className={`status-badge status-${wheelData.status}`}>
                                                {wheelData.status === 'wfa' ? 'Waiting For Approval' : wheelData.status}
                                            </span>
                                        </div>
                                    </div>
                                    {/* <div className="detail-item">
                                        <div className="detail-label">Prize</div>
                                        <div className="detail-value">{formatPrice(wheelData.prize)}</div>
                                    </div> */}
                                    <div className="detail-item">
                                        <div className="detail-label">Spin Date</div>
                                        <div className="detail-value">{formatDate(wheelData.spinning_time)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Description</div>
                                        <div className="detail-value">{wheelData.description || '-'}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Minimum Count</div>
                                        <div className="detail-value">{wheelData.minimum_count || '-'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h2 className="section-title">Wheel Options</h2>
                                <div className="detail-item">
                                    <div className="detail-label">Pies</div>
                                    <div className="pies-container">
                                        {wheelData.pies.map((pie, index) => (
                                            <span key={index} className="pie-item">{pie}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h2 className="section-title">Additional Information</h2>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">Created Date</div>
                                        <div className="detail-value">{formatDate(wheelData.created_date)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Last Updated</div>
                                        <div className="detail-value">{formatDate(wheelData.updated_date)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Winner Text</div>
                                        <div className="detail-value">{wheelData.winner_text || '-'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WheelDetails;